/*
#############################################################################
# Copyright (c) 2022-2023 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
#
# NVIDIA CORPORATION, its affiliates and licensors retain all intellectual
# property and proprietary rights in and to this material, related
# documentation and any modifications thereto. Any use, reproduction,
# disclosure or distribution of this material and related documentation
# without an express license agreement from NVIDIA CORPORATION or
# its affiliates is strictly prohibited.
###############################################################################
*/

/* eslint-disable no-console */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import config, { HTTP_STATUS } from "../../config/config";
import { webSocketActions } from "../../connection/websocket/websocketSlice";

const cartInitialState = {
  checkoutCartLoadingStatus: null,
  postCartLoadingStatus: null,
  getCartLoadingStatus: null,
  data: [] /* response of HTTP request */,
  totalBill: 0,
  totalCalories: 0,
};

export const getCart = createAsyncThunk(
  "tokkio/getCart",
  async (_, { getState }) => {
    const state = getState();
    const response = await axios(
      `${config.uiServerEndpoint}/cart/${state.uuid.sessionId}`
    );
    return response.data;
  }
);

export const postCartItems = createAsyncThunk(
  "tokkio/postCartItems",
  async (item, { getState, dispatch }) => {
    const state = getState();
    const postPayload = {
      items: [
        {
          item_id: item.item_id,
          toppings: item.toppings
            ? item.toppings
            .filter((topping) => topping.quantity === 1 && topping.item_id)
            .map((topping) => ({item_id: topping.item_id}))
            : [],
          size: item.size,
          quantity: 1,
        },
      ],
    };
    console.log("postPayload", postPayload);

    const { data } = await axios(
      `${config.uiServerEndpoint}/cart/${state.uuid.sessionId}`,
      {
        method: "post",
        data: postPayload,
      }
    );
    console.info("Successful POST request for data", postPayload);
    dispatch(getCart());
    return data;
  }
);

export const checkoutCart = createAsyncThunk(
  "tokkio/checkoutCart",
  async (_, { getState, dispatch }) => {
    const state = getState();
    const { data } = await axios(
      `${config.uiServerEndpoint}/cart/checkout/${state.uuid.sessionId}`,
      {
        method: "post",
      }
    ); 
    console.info("Successful POST checkout request for cart", state.cart.data)
    dispatch(webSocketActions.setSessionActive(false));
    return data;
  }
);

const cartSlice = createSlice({
  name: "cart",
  initialState: cartInitialState,
  reducers: {
    emptyCart(state) {
      state.data = [];
      state.totalBill = 0;
      state.totalCalories = 0;
    },
  },
  extraReducers: {
    [checkoutCart.pending](state) {
      state.checkoutCartLoadingStatus = HTTP_STATUS.PENDING;
    },
    [checkoutCart.fulfilled](state) {
      state.checkoutCartLoadingStatus = HTTP_STATUS.FULFILLED;
    },
    [checkoutCart.rejected](state) {
      state.checkoutCartLoadingStatus = HTTP_STATUS.REJECTED;
    },
    [postCartItems.pending](state) {
      state.postCartLoadingStatus = HTTP_STATUS.PENDING;
    },
    [postCartItems.fulfilled](state) {
      state.postCartLoadingStatus = HTTP_STATUS.FULFILLED;
    },
    [postCartItems.rejected](state) {
      state.postCartLoadingStatus = HTTP_STATUS.REJECTED;
    },
    [getCart.pending](state) {
      state.getCartLoadingStatus = HTTP_STATUS.PENDING;
    },
    [getCart.fulfilled](state, { payload }) {
      state.getCartLoadingStatus = HTTP_STATUS.FULFILLED;
      state.data = payload.items;
      state.totalBill = payload.total_bill;
      state.totalCalories = payload.total_calories;
    },
    [getCart.rejected](state) {
      state.getCartLoadingStatus = HTTP_STATUS.REJECTED;
    },
  },
});

export const cartActions = cartSlice.actions;
export default cartSlice;
